import type { ResolvedPlan } from './resolve';
import type { EstimateAssumptions } from './types';

const SECONDS_PER_DAY = 86400;

export interface LevelEstimate {
  /** `-1` for the raw bucket, otherwise the tier index. */
  index: number;
  bucket: string;
  /** Short label for charts and the diagram, e.g. `raw · 10s`. */
  label: string;
  /** Sample resolution in seconds. */
  resolutionSeconds: number;
  /** Distinct series written at this level. */
  seriesCount: number;
  /** Points written per series per day. */
  pointsPerSeriesPerDay: number;
  /** Points written across all series per day. */
  pointsPerDay: number;
  /** Bytes written per day at the assumed bytes-per-point. */
  bytesPerDay: number;
  /** Retention in days; infinite retention is priced at the horizon. */
  retentionDays: number;
  /** True when the bucket retention is infinite and was clamped to the horizon. */
  retentionClamped: boolean;
  /** Bytes resident once the bucket has filled its retention window. */
  steadyStateBytes: number;
}

export interface GrowthPoint {
  day: number;
  /** Bytes resident if raw data were kept for the whole horizon. */
  rawOnly: number;
  /** Bytes resident under the tiered design. */
  tiered: number;
}

export interface StorageEstimate {
  raw: LevelEstimate;
  tiers: LevelEstimate[];
  /** Raw plus every tier, in cascade order — convenient for charts. */
  levels: LevelEstimate[];
  horizonDays: number;
  bytesPerPoint: number;
  /** Steady-state bytes if raw were simply retained for the whole horizon. */
  rawOnlyBytes: number;
  /** Steady-state bytes for the tiered design (raw at its own retention + tiers). */
  tieredBytes: number;
  /** `rawOnlyBytes - tieredBytes`; negative if the design costs more than it saves. */
  savedBytes: number;
  /** Saving as a fraction of the raw-only baseline, in the range (-inf, 1]. */
  savedFraction: number;
  /** Resident bytes over time for both designs, for the growth chart. */
  growth: GrowthPoint[];
}

/** Retention in days, substituting the horizon for infinite retention. */
function retentionDays(seconds: number, horizonDays: number): { days: number; clamped: boolean } {
  if (!Number.isFinite(seconds)) return { days: horizonDays, clamped: true };
  return { days: Math.min(seconds / SECONDS_PER_DAY, horizonDays), clamped: false };
}

function level(
  index: number,
  bucket: string,
  labelSuffix: string,
  resolutionSeconds: number,
  seriesCount: number,
  retentionSeconds: number,
  assumptions: EstimateAssumptions,
): LevelEstimate {
  const pointsPerSeriesPerDay = SECONDS_PER_DAY / resolutionSeconds;
  const pointsPerDay = pointsPerSeriesPerDay * seriesCount;
  const bytesPerDay = pointsPerDay * assumptions.bytesPerPoint;
  const { days, clamped } = retentionDays(retentionSeconds, assumptions.horizonDays);
  return {
    index,
    bucket,
    label: `${bucket} · ${labelSuffix}`,
    resolutionSeconds,
    seriesCount,
    pointsPerSeriesPerDay,
    pointsPerDay,
    bytesPerDay,
    retentionDays: days,
    retentionClamped: clamped,
    steadyStateBytes: bytesPerDay * days,
  };
}

/**
 * Cost a resolved plan.
 *
 * The model is deliberately simple enough to check by hand:
 *
 * ```text
 * points/day  = (86400 / resolution) × series
 * bytes/day   = points/day × bytesPerPoint
 * resident    = bytes/day × retentionDays
 * ```
 *
 * Series counts follow the cascade. The raw bucket carries every tag combination;
 * a rollup only carries the *retained* tags, but each aggregate function in a tier
 * multiplies the series count, and that multiplication compounds down the cascade —
 * a 3-aggregate minute tier feeding a 2-aggregate hour tier writes 6 series per
 * input series. That compounding is usually the reason a rollup bucket costs more
 * than its owner expected.
 *
 * `bytesPerPoint` is an assumption, not a measurement: real TSM compression depends
 * on the value distribution and can range from under 2 bytes for slow-moving
 * counters to well over 16 for jittery floats. It is an input for exactly that reason.
 */
export function estimateStorage(
  plan: ResolvedPlan,
  assumptions: EstimateAssumptions,
): StorageEstimate {
  const horizonDays = Math.max(1, assumptions.horizonDays);
  const bounded: EstimateAssumptions = { ...assumptions, horizonDays };

  const raw = level(
    -1,
    plan.source.bucket,
    'raw',
    plan.source.intervalSeconds,
    plan.source.seriesCount,
    plan.source.retentionSeconds,
    bounded,
  );

  let seriesMultiplier = 1;
  const tiers = plan.tiers.map((tier) => {
    seriesMultiplier *= tier.aggregates.length;
    return level(
      tier.index,
      tier.bucket,
      tier.window,
      tier.windowSeconds,
      plan.source.retainedSeriesCount * seriesMultiplier,
      tier.retentionSeconds,
      bounded,
    );
  });

  const levels = [raw, ...tiers];
  const rawOnlyBytes = raw.bytesPerDay * horizonDays;
  const tieredBytes = levels.reduce((sum, l) => sum + l.steadyStateBytes, 0);
  const savedBytes = rawOnlyBytes - tieredBytes;

  return {
    raw,
    tiers,
    levels,
    horizonDays,
    bytesPerPoint: assumptions.bytesPerPoint,
    rawOnlyBytes,
    tieredBytes,
    savedBytes,
    savedFraction: rawOnlyBytes === 0 ? 0 : savedBytes / rawOnlyBytes,
    growth: growthCurve(levels, raw, horizonDays),
  };
}

/**
 * Resident bytes on day `t`. Each bucket grows linearly until it reaches its
 * retention window, then plateaus as expiry balances ingest.
 */
function growthCurve(
  levels: LevelEstimate[],
  raw: LevelEstimate,
  horizonDays: number,
): GrowthPoint[] {
  const steps = 120;
  const points: GrowthPoint[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const day = (horizonDays * i) / steps;
    points.push({
      day,
      rawOnly: raw.bytesPerDay * day,
      tiered: levels.reduce((sum, l) => sum + l.bytesPerDay * Math.min(day, l.retentionDays), 0),
    });
  }
  return points;
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

/** Format bytes with three significant figures, e.g. `1.42 TB`. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return '—';
  const sign = bytes < 0 ? '-' : '';
  let value = Math.abs(bytes);
  let unit = 0;
  while (value >= 1000 && unit < UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${sign}${value.toFixed(unit === 0 ? 0 : decimals)} ${UNITS[unit]}`;
}

/** Format a plain count with thin thousands separators. */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e4) return `${(n / 1e3).toFixed(1)}k`;
  return Math.round(n).toLocaleString('en-US');
}

/** Format a fraction as a signed percentage, e.g. `98.7%`. */
export function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return '—';
  return `${(fraction * 100).toFixed(1)}%`;
}
